import {ValueEnabledModel} from '../../common/ValueEnabled.model';
import {ValueEnabledTitleModel} from '../../common/ValueEnabledTitle.model';
import {DestinationScope, StoreCheckoutSettingsAndPayments} from '../../../utils/types.util';
import {PaymentMethod} from '@wix/wixstores-graphql-schema-node';
import {type CountryCode} from '@wix/form-multiline-address';
import {getEnabledDeliveryCountries} from '../../../utils/destinations.utils';
import {CheckoutHeaderModel} from '../../common/CheckoutHeader.model';
import {EcommerceSettings} from '@wix/wixstores-client-storefront-sdk';

export class CheckoutSettingsModel {
  public storeName?: string;
  public logoUrl?: string;
  public taxName: string;
  public taxOnProduct: boolean;
  public termsAndConditions: ValueEnabledModel;
  public privacyPolicy: ValueEnabledModel;
  public returnPolicy: ValueEnabledModel;
  public digitalPolicy: ValueEnabledModel;
  public contactUs: ValueEnabledModel;
  public customPolicy: ValueEnabledTitleModel;
  public arePoliciesCheckedByDefault: boolean;
  public areGeneralPoliciesEnabled: boolean;
  public shouldShowPoliciesCheckbox: boolean;
  public isThereAnyPolicyEnabled: boolean;
  public isDigitalPolicyEnabled: boolean;
  public isCustomPolicyEnabled: boolean;
  public isSubscriptionCheckedByDefault: boolean;
  public isSubscriptionEnabled: boolean;
  public isGiftCardEnabled: boolean;
  public isGiftCardSupported: boolean;
  public paymentProviders?: string;
  public activePaymentMethods?: PaymentMethod[];
  public isLocalDeliveryByZipCodeEnabled: boolean;
  public mitEnabled: boolean;
  public delayCaptureEnabled?: boolean;
  public enabledDeliveryCountries: CountryCode[];
  public checkoutHeader?: CheckoutHeaderModel;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  constructor({
    checkoutSettingsAndPayments,
    taxName,
    ecommerceSettings,
    shouldUseEcommerceSettings,
  }: {
    checkoutSettingsAndPayments?: StoreCheckoutSettingsAndPayments;
    taxName: string;
    ecommerceSettings?: EcommerceSettings;
    shouldUseEcommerceSettings: boolean;
  }) {
    if (shouldUseEcommerceSettings) {
      this.storeName = ecommerceSettings?.businessInfo?.storeName ?? undefined;
      this.logoUrl = ecommerceSettings?.businessInfo?.logo ?? undefined;
      this.taxOnProduct = Boolean(ecommerceSettings?.tax?.taxIncludedInItemPrices);
      this.termsAndConditions = new ValueEnabledModel({
        enabled: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.termsAndConditions?.visible,
        value: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.termsAndConditions?.content,
      });
      this.privacyPolicy = new ValueEnabledModel({
        enabled: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.privacyPolicy?.visible,
        value: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.privacyPolicy?.content,
      });
      this.returnPolicy = new ValueEnabledModel({
        enabled: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.returnPolicy?.visible,
        value: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.returnPolicy?.content,
      });
      this.digitalPolicy = new ValueEnabledModel({
        enabled: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.digitalItemPolicy?.visible,
        value: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.digitalItemPolicy?.content,
      });
      this.contactUs = new ValueEnabledModel({
        enabled: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.contactUs?.visible,
        value: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.contactUs?.content,
      });
      this.customPolicy = new ValueEnabledTitleModel({
        enabled: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.customPolicy?.visible,
        value: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.customPolicy?.content,
        title: ecommerceSettings?.checkoutSettings?.checkoutPolicies?.customPolicy?.title,
      });
      this.arePoliciesCheckedByDefault = Boolean(
        ecommerceSettings?.checkoutSettings?.checkoutFields?.policyAgreementCheckbox?.checkedByDefault
      );
      this.shouldShowPoliciesCheckbox = Boolean(
        ecommerceSettings?.checkoutSettings?.checkoutFields?.policyAgreementCheckbox?.visible
      );
      this.isDigitalPolicyEnabled = this.shouldShowPoliciesCheckbox && this.digitalPolicy.enabled;
      this.isCustomPolicyEnabled = this.shouldShowPoliciesCheckbox && this.customPolicy.enabled;
      this.isSubscriptionCheckedByDefault = Boolean(
        ecommerceSettings?.checkoutSettings?.checkoutFields?.subscriptionCheckbox?.checkedByDefault
      );
      this.isSubscriptionEnabled = Boolean(
        ecommerceSettings?.checkoutSettings?.checkoutFields?.subscriptionCheckbox?.visible
      );
      this.isGiftCardEnabled = Boolean(ecommerceSettings?.checkoutSettings?.checkoutFields?.giftCardRedeemEnabled);
      this.isGiftCardSupported = Boolean(ecommerceSettings?.checkoutSettings?.checkoutFields?.giftCardRedeemEnabled);
      this.mitEnabled = Boolean(ecommerceSettings?.checkoutSettings?.checkoutFields?.mitEnabled);
      this.delayCaptureEnabled = Boolean(ecommerceSettings?.checkoutSettings?.checkoutFields?.delayCaptureEnabled);
      this.isLocalDeliveryByZipCodeEnabled = Boolean(ecommerceSettings?.delivery?.postalCodeRequired);
      this.enabledDeliveryCountries = getEnabledDeliveryCountriesFromEcommerceSettings(ecommerceSettings);
      this.checkoutHeader = ecommerceSettings?.checkoutSettings?.checkoutBrand?.header
        ? new CheckoutHeaderModel({
            alignment: ecommerceSettings?.checkoutSettings?.checkoutBrand?.header.alignment,
            displayCheckoutLabel: ecommerceSettings?.checkoutSettings?.checkoutBrand?.header.displayCheckoutLabel,
            logo: {
              size: ecommerceSettings?.checkoutSettings?.checkoutBrand?.header.logo?.size,
            },
          })
        : /* istanbul ignore next */ undefined;
    } else {
      const checkoutSettings = checkoutSettingsAndPayments!.checkoutSettings;
      this.storeName = checkoutSettings.storeName ?? undefined;
      this.logoUrl = checkoutSettings.logo?.url;
      this.taxOnProduct = checkoutSettings.taxOnProduct ?? false;
      this.termsAndConditions = new ValueEnabledModel(checkoutSettings.termsAndConditions);
      this.privacyPolicy = new ValueEnabledModel(checkoutSettings.privacyPolicy);
      this.returnPolicy = new ValueEnabledModel(checkoutSettings.refundCancellationPolicy);
      this.digitalPolicy = new ValueEnabledModel(checkoutSettings.digitalItemPolicy);
      this.contactUs = new ValueEnabledTitleModel(checkoutSettings.contactUs);
      this.customPolicy = new ValueEnabledTitleModel(checkoutSettings.customPolicy);
      this.arePoliciesCheckedByDefault = !!checkoutSettings.checkoutPolicyCheckbox?.checked;
      this.shouldShowPoliciesCheckbox = !!checkoutSettings.checkoutPolicyCheckbox?.show;
      this.isDigitalPolicyEnabled = this.shouldShowPoliciesCheckbox && this.digitalPolicy.enabled;
      this.isCustomPolicyEnabled = this.shouldShowPoliciesCheckbox && this.customPolicy.enabled;
      this.isSubscriptionCheckedByDefault = !!checkoutSettings.checkoutSubscriptionCheckbox?.checked;
      this.isSubscriptionEnabled = !!checkoutSettings.checkoutSubscriptionCheckbox?.show;
      this.isGiftCardEnabled = !!checkoutSettings.checkoutGiftCardCheckbox?.show;
      this.isGiftCardSupported = !!checkoutSettings.checkoutGiftCardCheckbox?.supported;
      this.activePaymentMethods = checkoutSettingsAndPayments!.payments.activePaymentMethods;
      this.paymentProviders = this.activePaymentMethods.map((paymentMethod) => paymentMethod.name).join(',');
      this.isLocalDeliveryByZipCodeEnabled =
        checkoutSettingsAndPayments?.localDeliveryByZipCodeFlag?.localDeliveryByZipCodeExists ?? false;
      this.mitEnabled = checkoutSettings.mitEnabled ?? false;
      this.delayCaptureEnabled = checkoutSettings.delayCaptureEnabled ?? false;
      this.enabledDeliveryCountries = getEnabledDeliveryCountries(checkoutSettingsAndPayments?.destinations);
      this.checkoutHeader = checkoutSettings?.checkoutHeader
        ? new CheckoutHeaderModel(checkoutSettings?.checkoutHeader)
        : /* istanbul ignore next */ undefined;
    }
    this.isThereAnyPolicyEnabled =
      this.termsAndConditions.enabled ||
      this.privacyPolicy.enabled ||
      this.returnPolicy.enabled ||
      this.customPolicy.enabled;
    this.areGeneralPoliciesEnabled = this.shouldShowPoliciesCheckbox && this.isThereAnyPolicyEnabled;
    this.taxName = taxName;
  }
}

function getEnabledDeliveryCountriesFromEcommerceSettings(ecommerceSettings?: EcommerceSettings): CountryCode[] {
  const destinations =
    ecommerceSettings?.delivery?.destinationScope === DestinationScope.WORLDWIDE
      ? []
      : ecommerceSettings?.delivery?.destinations
          ?.map((destination) => destination.countryCode)
          .filter((code): code is CountryCode => !!code) ?? /* istanbul ignore next */ [];

  return [...new Set(destinations)];
}
